import {
  APP_EVENT_BATCH_LIMIT,
  normalizeScreen,
  type AppEventName,
} from '@etap/shared';

/**
 * Первичный поток продуктовых событий: пишем в свою базу, ключ — user_id.
 *
 * Метрика остаётся (см. lib/analytics.ts), но отвечает на другие вопросы.
 * Её события не связываются с конкретным пользователем и не джойнятся с
 * расходами, поэтому «почему этот человек перестал ставить теги» по ней
 * не выяснить — а по этому потоку выяснить можно.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const SESSION_KEY = 'etap_event_session';
/** Пауза, после которой считаем, что началась новая сессия. */
const SESSION_IDLE_MS = 30 * 60 * 1000;
/** Задержка перед отправкой: копим события, чтобы не слать по одному. */
const FLUSH_DEBOUNCE_MS = 5_000;
/** Предохранитель: если сеть лежит, очередь не должна расти бесконечно. */
const MAX_QUEUE = 200;

type PropValue = string | number | boolean | null;

interface QueuedEvent {
  name: AppEventName;
  sessionId: string;
  screen?: string;
  projectId?: string;
  props?: Record<string, PropValue>;
  occurredAt: string;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersAttached = false;

const isBrowser = typeof window !== 'undefined';

const readToken = (): string | null => {
  try {
    return window.localStorage.getItem('etap_token');
  } catch {
    return null;
  }
};

const getClientSource = (): 'telegram' | 'webapp' => {
  const tg = window.Telegram?.WebApp;
  return tg && tg.initData ? 'telegram' : 'webapp';
};

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * Идентификатор сессии живёт в sessionStorage и протухает по бездействию.
 * sessionStorage (а не localStorage) — потому что сессия и должна умирать
 * вместе со вкладкой; таймаут добавлен для Mini App, который вкладку не
 * закрывает неделями.
 */
function getSessionId(): string {
  const fallback = () => createId();
  if (!isBrowser) return fallback();

  const now = Date.now();

  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { id: string; touchedAt: number };
      if (parsed?.id && now - parsed.touchedAt < SESSION_IDLE_MS) {
        window.sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ id: parsed.id, touchedAt: now }),
        );
        return parsed.id;
      }
    }
  } catch {
    // Приватный режим или заблокированное хранилище — работаем без него.
  }

  const id = createId();
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, touchedAt: now }));
  } catch {
    // Ignore.
  }
  return id;
}

/** Была ли сессия начата заново прямо сейчас (для session_started). */
function isFreshSession(): boolean {
  if (!isBrowser) return false;
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return true;
    const parsed = JSON.parse(raw) as { touchedAt: number };
    return Date.now() - parsed.touchedAt >= SESSION_IDLE_MS;
  } catch {
    return true;
  }
}

async function flush(useKeepalive = false): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (queue.length === 0) return;

  const token = readToken();
  // До авторизации отправлять некуда: поток по определению user-scoped.
  // Держим события в очереди — сразу после логина они уедут первой пачкой.
  if (!token) return;

  const batch = queue.slice(0, APP_EVENT_BATCH_LIMIT);

  try {
    const response = await fetch(`${API_BASE_URL}/tracking/app-events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Client-Source': getClientSource(),
      },
      body: JSON.stringify({
        source: getClientSource(),
        appVersion: import.meta.env.VITE_APP_VERSION || undefined,
        events: batch,
      }),
      // keepalive позволяет запросу пережить закрытие вкладки; лимит тела
      // 64 КБ, поэтому шлём именно пачками, а не всей очередью разом.
      keepalive: useKeepalive,
    });

    if (!response.ok) {
      // 400 — событие не пройдёт валидацию и на второй раз: выбрасываем,
      // иначе битая запись навсегда заблокирует голову очереди.
      if (response.status === 400) {
        queue = queue.slice(batch.length);
      }
      return;
    }

    queue = queue.slice(batch.length);

    // Осталось ещё — досылаем, не дожидаясь дебаунса.
    if (queue.length > 0) scheduleFlush(0);
  } catch {
    // Сеть отвалилась — события остаются в очереди до следующей попытки.
  }
}

function scheduleFlush(delay = FLUSH_DEBOUNCE_MS) {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => void flush(), delay);
}

function attachListeners() {
  if (listenersAttached || !isBrowser) return;
  listenersAttached = true;

  // pagehide надёжнее beforeunload на iOS, а visibilitychange ловит уход
  // в фон в Telegram Mini App, где вкладку никто не закрывает.
  window.addEventListener('pagehide', () => void flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
}

export interface TrackOptions {
  screen?: string;
  projectId?: string;
}

/** Кладёт событие в очередь. Никогда не бросает и не блокирует UI. */
export function trackAppEvent(
  name: AppEventName,
  props?: Record<string, PropValue>,
  options: TrackOptions = {},
): void {
  if (!isBrowser) return;

  try {
    attachListeners();

    if (queue.length >= MAX_QUEUE) {
      // Выкидываем самые старые: свежие события полезнее протухших.
      queue = queue.slice(queue.length - MAX_QUEUE + 1);
    }

    queue.push({
      name,
      sessionId: getSessionId(),
      screen: options.screen ?? normalizeScreen(window.location.pathname),
      projectId: options.projectId,
      props,
      occurredAt: new Date().toISOString(),
    });

    // Пачка набралась — не ждём дебаунса.
    scheduleFlush(queue.length >= APP_EVENT_BATCH_LIMIT ? 0 : FLUSH_DEBOUNCE_MS);
  } catch {
    // Аналитика не имеет права ломать приложение.
  }
}

/** Отмечает начало сессии. Вызывать один раз при старте приложения. */
export function trackSessionStart(props?: Record<string, PropValue>): void {
  if (!isBrowser) return;
  const fresh = isFreshSession();
  // getSessionId сам создаст новую сессию или продлит текущую.
  getSessionId();
  if (!fresh) return;

  trackAppEvent('session_started', {
    ...props,
    source: getClientSource(),
    locale: typeof navigator !== 'undefined' ? navigator.language : null,
  });
}

/** Принудительная отправка — например, сразу после успешного логина. */
export function flushAppEvents(): void {
  void flush();
}

export { normalizeScreen };
